/* =============================================================
   KIC v2 Mega Menu (Comp 28 port)
   APG disclosure pattern, NOT role="menu".
   - Click trigger toggles aria-expanded; panel mounts via [hidden] removal.
   - ESC closes panel and returns focus to trigger.
   - Tab cycles within panel; outside-click closes; focus returns to trigger.
   - rAF on open/close to avoid layout thrash (Wave A perf gain preserved).
   - prefers-reduced-motion: panel still functions, just no clip-path slide
     (CSS handles via media query).
   No deps. Vanilla JS module. ~150 LOC.
   ============================================================= */
(function () {
  'use strict';

  var doc = document;
  var openPanelEl = null;
  var openTriggerEl = null;

  function getPanelById(id) {
    return doc.getElementById(id);
  }

  function allTriggersFor(panelId) {
    return Array.prototype.slice.call(
      doc.querySelectorAll('[data-mega-trigger][aria-controls="' + panelId + '"]')
    );
  }

  function getFocusableInPanel(panel) {
    var sel = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
    return Array.prototype.slice.call(panel.querySelectorAll(sel));
  }

  function showBackdrop() {
    var bd = doc.querySelector('[data-mega-backdrop]');
    if (bd) bd.hidden = false;
  }

  function hideBackdrop() {
    var bd = doc.querySelector('[data-mega-backdrop]');
    if (bd) bd.hidden = true;
  }

  // R10.11 (2026-05-01) — serialize state mutations. Prior version queued
  // panel mutations inside requestAnimationFrame, but state vars (openPanelEl,
  // openTriggerEl) were ALSO updated inside the rAF. Rapid-click scenario:
  // user clicks trigger A, openPanel queues rAF; before rAF runs, user clicks
  // trigger B; closePanel reads openPanelEl=null, bails early; openPanel B
  // queues a second rAF. Both rAFs execute back-to-back; panel A and panel B
  // both end up with hidden=false. Fix: set state synchronously, defer DOM
  // writes only.
  function openPanel(panelId, sourceTrigger) {
    var panel = getPanelById(panelId);
    if (!panel) return;
    if (openPanelEl && openPanelEl !== panel) {
      closePanel(false);
    }
    // R10.11 — state set synchronously so a follow-up click to closePanel
    // sees the in-flight state.
    openPanelEl = panel;
    openTriggerEl = sourceTrigger || (allTriggersFor(panelId)[0]) || null;
    var trigs = allTriggersFor(panelId);
    trigs.forEach(function (t) { t.setAttribute('aria-expanded', 'true'); });
    Array.prototype.forEach.call(
      doc.querySelectorAll('[data-mega-trigger]'),
      function (t) {
        if (t.getAttribute('aria-controls') !== panelId) {
          t.setAttribute('aria-expanded', 'false');
        }
      }
    );
    requestAnimationFrame(function () {
      // Stale-rAF guard: bail if state has been re-pointed at a different panel.
      if (openPanelEl !== panel) return;
      panel.hidden = false;
      showBackdrop();
      var focusables = getFocusableInPanel(panel);
      if (focusables.length) {
        focusables[0].focus({ preventScroll: true });
      }
    });
  }

  function closePanel(returnFocus) {
    if (!openPanelEl) return;
    var panel = openPanelEl;
    var trigger = openTriggerEl;
    var panelId = panel.id;
    // R10.11 — state cleared synchronously.
    openPanelEl = null;
    openTriggerEl = null;
    allTriggersFor(panelId).forEach(function (t) {
      t.setAttribute('aria-expanded', 'false');
    });
    // A11Y-4 (Wave 2026-05-07): focus must return to trigger SYNCHRONOUSLY
    // before panel.hidden=true detaches the focused descendant. Previously
    // this ran inside rAF, which gave the browser a chance to fall back to
    // <body> when the focused element vanished from the DOM tree.
    if (returnFocus !== false && trigger && typeof trigger.focus === 'function') {
      trigger.focus({ preventScroll: true });
    }
    requestAnimationFrame(function () {
      // Stale-rAF guard: if a new panel opened in the same tick, do NOT close it.
      if (openPanelEl && openPanelEl !== panel) return;
      panel.hidden = true;
      hideBackdrop();
    });
  }

  function onTriggerClick(ev) {
    var btn = ev.currentTarget;
    var panelId = btn.getAttribute('aria-controls');
    if (!panelId) return;
    ev.preventDefault();
    var isOpen = btn.getAttribute('aria-expanded') === 'true';
    if (isOpen) {
      closePanel(true);
    } else {
      openPanel(panelId, btn);
    }
  }

  function onKeyDown(ev) {
    if (!openPanelEl) return;
    if (ev.key === 'Escape' || ev.key === 'Esc') {
      ev.preventDefault();
      closePanel(true);
      return;
    }
    if (ev.key === 'Tab') {
      // Trap Tab inside panel: last -> first cycle.
      var focusables = getFocusableInPanel(openPanelEl);
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      var active = doc.activeElement;
      if (ev.shiftKey && active === first) {
        ev.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!ev.shiftKey && active === last) {
        ev.preventDefault();
        first.focus({ preventScroll: true });
      }
    }
  }

  function onDocumentClick(ev) {
    if (!openPanelEl) return;
    var t = ev.target;
    // Click within an open panel: do nothing (let link/close button handle it).
    if (openPanelEl.contains(t)) return;
    // Click on any trigger: handled by trigger click handler.
    if (t.closest && t.closest('[data-mega-trigger]')) return;
    // Otherwise outside-click: close.
    closePanel(true);
  }

  function bindCloseButtons() {
    Array.prototype.forEach.call(
      doc.querySelectorAll('[data-mega-close]'),
      function (btn) {
        btn.addEventListener('click', function (ev) {
          ev.preventDefault();
          closePanel(true);
        });
      }
    );
  }

  function bindTriggers() {
    Array.prototype.forEach.call(
      doc.querySelectorAll('[data-mega-trigger]'),
      function (btn) {
        btn.addEventListener('click', onTriggerClick);
      }
    );
  }

  function bindBackdrop() {
    var bd = doc.querySelector('[data-mega-backdrop]');
    if (!bd) return;
    bd.addEventListener('click', function () { closePanel(true); });
  }

  function init() {
    bindTriggers();
    bindCloseButtons();
    bindBackdrop();
    doc.addEventListener('keydown', onKeyDown);
    doc.addEventListener('click', onDocumentClick);
  }

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
